import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ContentLayout from "@cloudscape-design/components/content-layout";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Button from "@cloudscape-design/components/button";
import { listSessions } from "../services/sessionApi";

export function isWithinTimeRange(createdAt, rangeValue) {
  if (rangeValue === "all") return true;
  const now = Date.now();
  const created = new Date(createdAt).getTime();
  if (isNaN(created)) return false;
  const ranges = {
    "last-hour": 60 * 60 * 1000,
    "last-day": 24 * 60 * 60 * 1000,
    "last-7-days": 7 * 24 * 60 * 60 * 1000,
    "last-30-days": 30 * 24 * 60 * 60 * 1000,
  };
  const threshold = ranges[rangeValue];
  if (threshold === undefined) return true;
  return (now - created) <= threshold;
}

export function formatDuration(createdAt, updatedAt) {
  if (!createdAt || !updatedAt) return "\u2014";
  const start = new Date(createdAt);
  const end = new Date(updatedAt);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return "\u2014";
  const diffMs = end - start;
  if (diffMs < 0) return "\u2014";
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function SessionsView({ onNavigate }) {
  const { t, i18n } = useTranslation();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState({
    label: t("sessions.filters.all_scenarios"),
    value: "all",
  });
  const [selectedTimeRange, setSelectedTimeRange] = useState({
    label: t("sessions.filters.all"),
    value: "all",
  });

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSessions();
      setSessions(data || []);
    } catch (err) {
      setError(err.message || t("sessions.errors.fetch_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const scenarioOptions = [
    { label: t("sessions.filters.all_scenarios"), value: "all" },
    ...[...new Set(sessions.map((s) => s.scenario_name).filter(Boolean))].map(
      (name) => ({ label: name, value: name })
    ),
  ];

  const timeRangeOptions = [
    { label: t("sessions.filters.all"), value: "all" },
    { label: t("sessions.filters.last_hour"), value: "last-hour" },
    { label: t("sessions.filters.last_day"), value: "last-day" },
    { label: t("sessions.filters.last_7_days"), value: "last-7-days" },
    { label: t("sessions.filters.last_30_days"), value: "last-30-days" },
  ];

  const filteredSessions = sessions.filter((s) => {
    const matchesScenario =
      selectedScenario.value === "all" || s.scenario_name === selectedScenario.value;
    const matchesTimeRange = isWithinTimeRange(s.created_at, selectedTimeRange.value);
    return matchesScenario && matchesTimeRange;
  });

  const columnDefinitions = [
    {
      id: "start_time",
      header: t("sessions.table.start_time"),
      cell: (item) => new Date(item.created_at).toLocaleString(i18n.language),
      sortingField: "created_at",
    },
    {
      id: "duration",
      header: t("sessions.table.duration"),
      cell: (item) => formatDuration(item.created_at, item.updated_at),
    },
    {
      id: "scenario_name",
      header: t("sessions.table.scenario_name"),
      cell: (item) => item.scenario_name,
      sortingField: "scenario_name",
    },
    {
      id: "calling_number",
      header: t("sessions.table.calling_number"),
      cell: (item) => item.metadata?.from_number || "\u2014",
    },
    {
      id: "called_number",
      header: t("sessions.table.called_number"),
      cell: (item) => item.metadata?.to_number || "\u2014",
    },
  ];

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("sessions.view.description")}
        >
          {t("sessions.view.title")}
        </Header>
      }
    >
      <Container>
        <SpaceBetween size="l">
          {error && (
            <Alert
              type="error"
              dismissible
              onDismiss={() => setError(null)}
              action={<Button onClick={fetchSessions}>{t("errors.api.try_again")}</Button>}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner size="large" />
              <Box variant="p" margin={{ top: "s" }}>{t("sessions.view.loading")}</Box>
            </div>
          ) : (
            <>
              <SpaceBetween direction="horizontal" size="m">
                <Select
                  selectedOption={selectedScenario}
                  onChange={({ detail }) =>
                    setSelectedScenario(detail.selectedOption)
                  }
                  options={scenarioOptions}
                  placeholder={t("sessions.filters.scenario")}
                />
                <Select
                  selectedOption={selectedTimeRange}
                  onChange={({ detail }) =>
                    setSelectedTimeRange(detail.selectedOption)
                  }
                  options={timeRangeOptions}
                  placeholder={t("sessions.filters.time_range")}
                />
              </SpaceBetween>
              <Table
                columnDefinitions={columnDefinitions}
                items={filteredSessions}
                variant="full-page"
                onRowClick={({ detail }) => {
                  onNavigate("session-detail", { session: detail.item });
                }}
                empty={
                  <Box textAlign="center" padding={{ vertical: "xxl" }}>
                    <SpaceBetween size="m">
                      {selectedScenario.value !== "all" || selectedTimeRange.value !== "all" ? (
                        <>
                          <Box variant="h2" color="text-body-secondary">
                            {t("sessions.view.no_matches_title")}
                          </Box>
                          <Box variant="p" color="text-body-secondary">
                            {t("sessions.view.no_matches_message")}
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box variant="h2" color="text-body-secondary">
                            {t("sessions.view.empty_title")}
                          </Box>
                          <Box variant="p" color="text-body-secondary">
                            {t("sessions.view.empty_message")}
                          </Box>
                        </>
                      )}
                    </SpaceBetween>
                  </Box>
                }
                header={
                  <Header counter={`(${filteredSessions.length})`}>
                    {t("sessions.view.title")}
                  </Header>
                }
              />
            </>
          )}
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
}

export default SessionsView;
